import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  VideoRecorder,
  VideoRecorderCamera,
} from '@capacitor-community/video-recorder';
import axios from 'axios';
import Api from '../Api/Api.json';

const APP_NAME = 'semionhealth';
const TARGET_RECORD_SECONDS = 15;

const pad2 = (n) => String(n).padStart(2, '0');

const buildFaceVideoFileName = (uid, extension = 'mp4') => {
  const now = new Date();
  const stamp = [
    now.getFullYear(),
    pad2(now.getMonth() + 1),
    pad2(now.getDate()),
    pad2(now.getHours()),
    pad2(now.getMinutes()),
  ].join('_');
  const safeUid = String(uid || 'unknown').replace(/[^A-Za-z0-9_-]/g, '');
  const ext = String(extension || 'mp4').replace(/^\./, '');
  return `${APP_NAME}_${stamp}_${safeUid}.${ext}`;
};

const getUserDemographics = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user_info') || '{}');
    const ageRaw = user.age ?? user.Age;
    const age = ageRaw != null && ageRaw !== '' ? Number(ageRaw) : null;
    let gender = user.gender ?? user.Gender ?? '';
    gender = String(gender).trim().toUpperCase();
    if (gender === 'MALE' || gender === 'পুরুষ') gender = 'M';
    if (gender === 'FEMALE' || gender === 'মহিলা') gender = 'F';
    if (gender !== 'M' && gender !== 'F') gender = '';
    const uid = user.di_id ?? user.phone ?? user.user_id ?? 'uid';
    return { age, gender, uid, user };
  } catch {
    return { age: null, gender: '', uid: 'uid', user: {} };
  }
};

/** Prefer MP4 when the browser supports it (Safari / some Chromium builds). */
const pickRecorderMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

const isMp4Blob = (blob) =>
  Boolean(blob?.type && /mp4|m4v|quicktime/i.test(blob.type));

let ffmpegInstance = null;
let ffmpegLoadPromise = null;

const getFFmpeg = async () => {
  if (ffmpegInstance?.loaded) return ffmpegInstance;
  if (ffmpegLoadPromise) return ffmpegLoadPromise;

  ffmpegLoadPromise = (async () => {
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');
    const ffmpeg = new FFmpeg();
    const baseURL = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegInstance = ffmpeg;
    return ffmpeg;
  })();

  try {
    return await ffmpegLoadPromise;
  } catch (e) {
    ffmpegLoadPromise = null;
    throw e;
  }
};

/** Chrome usually records WebM; re-encode to H.264 MP4 for the analysis pipeline. */
const ensureMp4Blob = async (blob, onStatus) => {
  if (!blob) throw new Error('No video blob to convert.');
  if (isMp4Blob(blob)) {
    return new Blob([blob], { type: 'video/mp4' });
  }

  if (onStatus) onStatus('Converting recording to MP4…');
  const { fetchFile } = await import('@ffmpeg/util');
  const ffmpeg = await getFFmpeg();
  const inputName = blob.type.includes('webm') ? 'input.webm' : 'input.bin';
  await ffmpeg.writeFile(inputName, await fetchFile(blob));
  // Re-encode to H.264 + AAC so OpenCV/Lambda can read the file reliably.
  await ffmpeg.exec([
    '-i',
    inputName,
    '-c:v',
    'libx264',
    '-preset',
    'ultrafast',
    '-pix_fmt',
    'yuv420p',
    '-c:a',
    'aac',
    '-movflags',
    '+faststart',
    'output.mp4',
  ]);
  const data = await ffmpeg.readFile('output.mp4');
  try {
    await ffmpeg.deleteFile(inputName);
    await ffmpeg.deleteFile('output.mp4');
  } catch (_) {
    /* ignore cleanup errors */
  }
  return new Blob([data.buffer], { type: 'video/mp4' });
};

const readVideoBlob = async (videoUrl, existingBlob) => {
  if (existingBlob && existingBlob.size > 0) return existingBlob;
  if (!videoUrl) throw new Error('No recorded video to upload.');

  try {
    const src = Capacitor.isNativePlatform()
      ? Capacitor.convertFileSrc(videoUrl)
      : videoUrl;
    const res = await fetch(src);
    if (res.ok) {
      const blob = await res.blob();
      if (blob && blob.size > 0) return blob;
    }
  } catch (e) {
    console.warn('fetch video failed, trying Filesystem', e);
  }

  try {
    const { Filesystem } = await import('@capacitor/filesystem');
    let path = videoUrl;
    if (path.startsWith('file://')) path = path.replace('file://', '');
    const contents = await Filesystem.readFile({ path });
    const b64 = contents.data;
    const binary = atob(typeof b64 === 'string' ? b64 : '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: 'video/mp4' });
  } catch (e) {
    console.error('Filesystem read failed', e);
    throw new Error('Unable to read the recorded video file.');
  }
};

const normalizeHrv = (hrv) => {
  if (!hrv) return null;
  if (typeof hrv === 'string') {
    try {
      return JSON.parse(hrv);
    } catch {
      return null;
    }
  }
  return typeof hrv === 'object' ? hrv : null;
};

const formatHrv = (hrv) => {
  const parsed = normalizeHrv(hrv);
  if (!parsed) return '—';
  const rmssd = parsed.rmssd_s != null ? Number(parsed.rmssd_s) : null;
  const sdnn = parsed.sdnn_s != null ? Number(parsed.sdnn_s) : null;
  const parts = [];
  if (rmssd != null && !Number.isNaN(rmssd)) {
    parts.push(`RMSSD ${(rmssd * 1000).toFixed(0)} ms`);
  }
  if (sdnn != null && !Number.isNaN(sdnn)) {
    parts.push(`SDNN ${(sdnn * 1000).toFixed(0)} ms`);
  }
  return parts.length ? parts.join(' · ') : '—';
};

const formatMetric = (value, digits = 0) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return digits > 0 ? n.toFixed(digits) : String(Math.round(n));
};

const formatUtcDatetime = () => {
  const d = new Date();
  return [
    d.getUTCFullYear(),
    '-',
    pad2(d.getUTCMonth() + 1),
    '-',
    pad2(d.getUTCDate()),
    ' ',
    pad2(d.getUTCHours()),
    ':',
    pad2(d.getUTCMinutes()),
    ':',
    pad2(d.getUTCSeconds()),
  ].join('');
};

const parseOptionalNumber = (value) => {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** True when at least one cuff/lab value was entered (partial calibration OK). */
const hasCalibrationData = (measurements) => {
  if (!measurements) return false;
  return ['HR', 'SBP', 'DBP', 'Glucose', 'HemoPct'].some(
    (k) => measurements[k] != null && !Number.isNaN(Number(measurements[k]))
  );
};

/** API rejects null measurement keys — fill blanks with 0. */
const measurementsWithDefaults = (measurements) => ({
  HR: measurements?.HR ?? 0,
  SBP: measurements?.SBP ?? 0,
  DBP: measurements?.DBP ?? 0,
  Glucose: measurements?.Glucose ?? 0,
  HemoPct: measurements?.HemoPct ?? 0,
});

const VideoExam = () => {
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Initializing camera…');
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [s3Path, setS3Path] = useState(null);
  const [ageInput, setAgeInput] = useState('');
  const [genderInput, setGenderInput] = useState('M');
  const [showCalibrationDialog, setShowCalibrationDialog] = useState(false);
  const [calibHR, setCalibHR] = useState('');
  const [calibSBP, setCalibSBP] = useState('');
  const [calibDBP, setCalibDBP] = useState('');
  const [calibGlucose, setCalibGlucose] = useState('');
  const [calibHemoPct, setCalibHemoPct] = useState('');
  const [submitAck, setSubmitAck] = useState(null);
  const [activeTab, setActiveTab] = useState('record'); // 'record' | 'previous'
  const [historyResults, setHistoryResults] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const durationTimerRef = useRef(null);
  const webPreviewRef = useRef(null);
  const webStreamRef = useRef(null);
  const webRecorderRef = useRef(null);
  const webChunksRef = useRef([]);
  const objectUrlRef = useRef(null);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    const { age, gender } = getUserDemographics();
    if (age != null && !Number.isNaN(age)) setAgeInput(String(age));
    if (gender) setGenderInput(gender);
  }, []);

  const clearDurationTimer = () => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  };

  const revokeObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  const stopWebStream = () => {
    if (webRecorderRef.current && webRecorderRef.current.state !== 'inactive') {
      try {
        webRecorderRef.current.stop();
      } catch (_) {
        /* ignore */
      }
    }
    webRecorderRef.current = null;
    if (webStreamRef.current) {
      webStreamRef.current.getTracks().forEach((t) => t.stop());
      webStreamRef.current = null;
    }
    if (webPreviewRef.current) {
      webPreviewRef.current.srcObject = null;
    }
  };

  const destroyRecorder = useCallback(async () => {
    clearDurationTimer();
    if (isNative) {
      try {
        await VideoRecorder.destroy();
      } catch (e) {
        // Ignore destroy errors when already torn down
      }
    } else {
      stopWebStream();
    }
  }, [isNative]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (activeTab !== 'record') {
        setIsReady(false);
        await destroyRecorder();
        return;
      }

      try {
        setError('');
        setStatus('Initializing camera…');

        if (isNative) {
          await VideoRecorder.initialize({
            camera: VideoRecorderCamera.FRONT,
            previewFrames: [
              {
                id: 'video-exam-preview',
                stackPosition: 'back',
                width: 'fill',
                height: 'fill',
                x: 0,
                y: 0,
                borderRadius: 0,
              },
            ],
          });
        } else {
          if (!navigator.mediaDevices?.getUserMedia) {
            throw new Error('Camera is not available in this browser.');
          }
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user' },
            audio: true,
          });
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          webStreamRef.current = stream;
          if (webPreviewRef.current) {
            webPreviewRef.current.srcObject = stream;
            await webPreviewRef.current.play().catch(() => {});
          }
        }

        if (!cancelled) {
          setIsReady(true);
          setStatus(
            `Camera ready. Keep your face in frame and record ~${TARGET_RECORD_SECONDS}s.`
          );
        }
      } catch (e) {
        console.error('Camera init error:', e);
        if (!cancelled) {
          setError(
            e?.message ||
              'Unable to initialize the camera. Camera and microphone permissions are required.'
          );
          setStatus('');
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      destroyRecorder();
    };
  }, [activeTab, destroyRecorder, isNative]);

  // Attach stream if the preview element mounts after getUserMedia
  useEffect(() => {
    if (
      activeTab === 'record' &&
      !isNative &&
      webStreamRef.current &&
      webPreviewRef.current
    ) {
      webPreviewRef.current.srcObject = webStreamRef.current;
      webPreviewRef.current.play().catch(() => {});
    }
  }, [activeTab, isNative, isReady]);

  const loadPreviousResults = useCallback(async () => {
    const { uid } = getUserDemographics();
    if (!uid || uid === 'uid') {
      setHistoryError('Sign in to view previous measurements.');
      setHistoryResults([]);
      return;
    }
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const resp = await axios.post(
        Api.FACE_VIDEO_UPLOAD_API,
        { task: 'getResults', userid: String(uid), limit: 20 },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = resp.data || {};
      if (data.error) throw new Error(data.error);
      setHistoryResults(Array.isArray(data.results) ? data.results : []);
    } catch (e) {
      console.error('getResults error:', e);
      setHistoryError(
        e?.response?.data?.error ||
          e?.message ||
          'Failed to load previous measurements.'
      );
      setHistoryResults([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'previous') {
      loadPreviousResults();
    }
  }, [activeTab, loadPreviousResults]);

  const startDurationTicker = () => {
    clearDurationTimer();
    durationTimerRef.current = setInterval(async () => {
      if (isNative) {
        try {
          const { value } = await VideoRecorder.getDuration();
          setDuration(value ?? 0);
          return;
        } catch (_) {
          /* fall through */
        }
      }
      setDuration((prev) => prev + 1);
    }, 1000);
  };

  const handleStartRecording = async () => {
    if (!isReady || isRecording || isUploading || isAnalyzing) return;
    try {
      setError('');
      setVideoUrl(null);
      setVideoBlob(null);
      setResults(null);
      setS3Path(null);
      setDuration(0);
      revokeObjectUrl();

      if (isNative) {
        await VideoRecorder.startRecording();
      } else {
        if (!webStreamRef.current) {
          throw new Error('Camera stream is not ready.');
        }
        webChunksRef.current = [];
        const mimeType = pickRecorderMimeType();
        const recorder = mimeType
          ? new MediaRecorder(webStreamRef.current, { mimeType })
          : new MediaRecorder(webStreamRef.current);
        webRecorderRef.current = recorder;
        recorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            webChunksRef.current.push(event.data);
          }
        };
        recorder.start(1000);
      }

      setIsRecording(true);
      setStatus(
        `Recording… Hold still for about ${TARGET_RECORD_SECONDS} seconds.`
      );
      startDurationTicker();
    } catch (e) {
      console.error('Start recording error:', e);
      setError(e?.message || 'Failed to start recording.');
    }
  };

  const handleStopRecording = async () => {
    if (!isRecording) return;
    try {
      clearDurationTimer();

      if (isNative) {
        const result = await VideoRecorder.stopRecording();
        setIsRecording(false);
        const url = result?.videoUrl || null;
        setVideoUrl(url);
        setVideoBlob(null);
        setStatus(
          url
            ? 'Recording saved. Tap Analyze BP to upload and process.'
            : 'Recording stopped.'
        );
        return;
      }

      const recorder = webRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        setIsRecording(false);
        setError('Recorder was not active.');
        return;
      }

      const blob = await new Promise((resolve, reject) => {
        recorder.onerror = (ev) => reject(ev.error || new Error('Recording failed'));
        recorder.onstop = () => {
          const type = recorder.mimeType || 'video/webm';
          resolve(new Blob(webChunksRef.current, { type }));
        };
        recorder.stop();
      });
      webRecorderRef.current = null;
      setIsRecording(false);

      if (!blob.size) {
        setError('Recording produced an empty file. Try again.');
        setStatus('');
        return;
      }

      let finalBlob = blob;
      try {
        finalBlob = await ensureMp4Blob(blob, setStatus);
      } catch (convErr) {
        console.error('MP4 conversion failed:', convErr);
        setError(
          convErr?.message ||
            'Could not convert recording to MP4. Try Safari, or record again.'
        );
        setStatus('');
        return;
      }

      revokeObjectUrl();
      const url = URL.createObjectURL(finalBlob);
      objectUrlRef.current = url;
      setVideoBlob(finalBlob);
      setVideoUrl(url);
      setStatus(
        duration < TARGET_RECORD_SECONDS
          ? `Saved ${duration}s MP4 (aim for ~${TARGET_RECORD_SECONDS}s). Tap Analyze BP.`
          : 'MP4 recording saved. Tap Analyze BP to upload and process.'
      );
    } catch (e) {
      console.error('Stop recording error:', e);
      setIsRecording(false);
      setError(e?.message || 'Failed to stop recording.');
    }
  };

  const handleFlipCamera = async () => {
    if (!isReady || isRecording) return;
    try {
      if (isNative) {
        await VideoRecorder.flipCamera();
        return;
      }
      // Web: restart stream with opposite facingMode
      const currentFacing =
        webStreamRef.current?.getVideoTracks()?.[0]?.getSettings?.()?.facingMode ||
        'user';
      const nextFacing = currentFacing === 'environment' ? 'user' : 'environment';
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: nextFacing } },
        audio: true,
      });
      stopWebStream();
      webStreamRef.current = stream;
      if (webPreviewRef.current) {
        webPreviewRef.current.srcObject = stream;
        await webPreviewRef.current.play().catch(() => {});
      }
    } catch (e) {
      console.error('Flip camera error:', e);
      setError(e?.message || 'Unable to flip camera.');
    }
  };

  const uploadAndAnalyze = async () => {
    if ((!videoUrl && !videoBlob) || isUploading || isAnalyzing) return;

    const age = Number(ageInput);
    const gender = String(genderInput || '').trim().toUpperCase();
    if (!age || Number.isNaN(age) || age < 1 || age > 120) {
      setError('Enter a valid age (1–120) before analyzing.');
      return;
    }
    if (gender !== 'M' && gender !== 'F') {
      setError('Select gender (M or F) before analyzing.');
      return;
    }

    const measurements = {
      HR: parseOptionalNumber(calibHR),
      SBP: parseOptionalNumber(calibSBP),
      DBP: parseOptionalNumber(calibDBP),
      Glucose: parseOptionalNumber(calibGlucose),
      HemoPct: parseOptionalNumber(calibHemoPct),
    };
    const useCalibration = hasCalibrationData(measurements);

    setError('');
    setResults(null);
    setSubmitAck(null);
    setIsUploading(true);
    setStatus('Reading recorded video…');

    try {
      const { uid } = getUserDemographics();
      const blob = await readVideoBlob(videoUrl, videoBlob);
      const mp4Blob = await ensureMp4Blob(blob, setStatus);
      const contentType = 'video/mp4';
      const fileName = buildFaceVideoFileName(uid, 'mp4');

      setStatus('Requesting upload URL…');
      const uploadResp = await axios.post(
        Api.FACE_VIDEO_UPLOAD_API,
        { task: 'upload', fileName, contentType },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const { uploadUrl, s3_video_file_path: s3Uri, contentType: putType } =
        uploadResp.data || {};
      if (!uploadUrl || !s3Uri) {
        throw new Error('Upload URL response was incomplete.');
      }

      setStatus('Uploading video to S3…');
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': putType || contentType },
        body: mp4Blob,
      });
      if (!putRes.ok) {
        const putText = await putRes.text().catch(() => '');
        throw new Error(
          `S3 upload failed (${putRes.status})${putText ? `: ${putText}` : ''}`
        );
      }
      setS3Path(s3Uri);
      setIsUploading(false);
      setIsAnalyzing(true);

      const task = useCalibration ? 'calibration' : 'inference';
      setStatus(
        useCalibration
          ? 'Submitting calibration job…'
          : 'Submitting inference job…'
      );

      const payload = {
        task,
        userid: String(uid),
        age,
        gender,
        s3_video_file_path: s3Uri,
        datetime: formatUtcDatetime(),
      };
      if (useCalibration) {
        const filled = measurementsWithDefaults(measurements);
        payload.HR = filled.HR;
        payload.SBP = filled.SBP;
        payload.DBP = filled.DBP;
        payload.Glucose = filled.Glucose;
        payload.HemoPct = filled.HemoPct;
      }

      const analyzeResp = await axios.post(Api.FACE_VIDEO_UPLOAD_API, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 60000,
      });

      const data = analyzeResp.data;
      if (data?.error) {
        throw new Error(data.error);
      }
      setSubmitAck(data);
      setResults(data?.hr != null || data?.sbp != null ? data : null);
      setStatus(
        useCalibration
          ? 'Calibration submitted. Results will be saved when processing finishes.'
          : 'Inference submitted. Results will be saved when processing finishes.'
      );
    } catch (e) {
      console.error('Upload/analyze error:', e);
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        'Upload or analysis failed.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setStatus('');
    } finally {
      setIsUploading(false);
      setIsAnalyzing(false);
    }
  };

  const saveCalibrationDialog = () => {
    const measurements = {
      HR: parseOptionalNumber(calibHR),
      SBP: parseOptionalNumber(calibSBP),
      DBP: parseOptionalNumber(calibDBP),
      Glucose: parseOptionalNumber(calibGlucose),
      HemoPct: parseOptionalNumber(calibHemoPct),
    };
    if (!hasCalibrationData(measurements)) {
      setError('Enter at least one calibration value (others can be left blank).');
      return;
    }
    setError('');
    setShowCalibrationDialog(false);
    setStatus(
      'Calibration data saved (blank fields sent as 0). Record a video, then tap Analyze BP.'
    );
  };

  const clearCalibrationData = () => {
    setCalibHR('');
    setCalibSBP('');
    setCalibDBP('');
    setCalibGlucose('');
    setCalibHemoPct('');
    setShowCalibrationDialog(false);
    setStatus('Calibration data cleared. Analyze BP will run inference.');
  };

  const handleBack = async () => {
    await destroyRecorder();
    revokeObjectUrl();
    navigate('/on-exam');
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${m}:${s}`;
  };

  const previewSrc =
    videoUrl && isNative ? Capacitor.convertFileSrc(videoUrl) : videoUrl;

  const busy = isUploading || isAnalyzing;
  const ppgSeries = Array.isArray(results?.ppg) ? results.ppg : [];
  const canAnalyze = Boolean(videoBlob || videoUrl);

  return (
    <div
      className={`min-h-screen video-exam-page pt-20 pb-8 px-4 ${
        isNative && activeTab === 'record' ? '' : 'bg-slate-900'
      }`}
      style={
        isNative && activeTab === 'record'
          ? { backgroundColor: 'transparent' }
          : undefined
      }
    >
      <div className="max-w-3xl mx-auto relative z-10">
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center text-white/90 hover:text-white mb-4 transition-colors"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to On Exam
          </button>
          <h1 className="text-3xl font-bold text-white drop-shadow">Video Exam</h1>
          <p className="text-white/80 mt-1 drop-shadow">
            Record ~{TARGET_RECORD_SECONDS}s of face video, then estimate PPG, HR,
            HRV, and blood pressure
          </p>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('record')}
            disabled={busy || isRecording}
            className={`flex-1 px-4 py-2.5 rounded-lg font-semibold transition-colors ${
              activeTab === 'record'
                ? 'bg-teal-500 text-white'
                : 'bg-white/15 text-white/80 hover:bg-white/25'
            } disabled:opacity-50`}
          >
            New Recording
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('previous')}
            disabled={busy || isRecording}
            className={`flex-1 px-4 py-2.5 rounded-lg font-semibold transition-colors ${
              activeTab === 'previous'
                ? 'bg-teal-500 text-white'
                : 'bg-white/15 text-white/80 hover:bg-white/25'
            } disabled:opacity-50`}
          >
            Previous Recordings
          </button>
        </div>

        {activeTab === 'previous' && (
          <div className="bg-white/95 rounded-xl shadow-lg p-5 mb-8 text-left">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2 className="text-xl font-semibold text-gray-900">
                Previous measurements
              </h2>
              <button
                type="button"
                onClick={loadPreviousResults}
                disabled={historyLoading}
                className="text-sm px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 font-medium hover:bg-teal-100 disabled:opacity-50"
              >
                {historyLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {historyLoading && (
              <p className="text-gray-500 text-sm">Loading last measurements…</p>
            )}
            {!historyLoading && historyError && (
              <p className="text-red-600 text-sm">{historyError}</p>
            )}
            {!historyLoading && !historyError && historyResults.length === 0 && (
              <p className="text-gray-500 text-sm">No previous measurements found.</p>
            )}
            {!historyLoading && historyResults.length > 0 && (
              <ul className="space-y-3">
                {historyResults.map((row, idx) => (
                  <li
                    key={`${row.timestamp || 'row'}-${idx}`}
                    className="rounded-lg border border-gray-200 p-4 bg-gray-50"
                  >
                    <div className="text-sm font-medium text-gray-800 mb-3">
                      {row.timestamp || 'Unknown time'}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="rounded-md bg-rose-50 p-2">
                        <div className="text-[10px] uppercase tracking-wide text-rose-700">
                          SBP
                        </div>
                        <div className="text-lg font-semibold text-rose-900">
                          {formatMetric(row.SBP)}
                          <span className="text-xs font-normal ml-1">mmHg</span>
                        </div>
                      </div>
                      <div className="rounded-md bg-blue-50 p-2">
                        <div className="text-[10px] uppercase tracking-wide text-blue-700">
                          DBP
                        </div>
                        <div className="text-lg font-semibold text-blue-900">
                          {formatMetric(row.DBP)}
                          <span className="text-xs font-normal ml-1">mmHg</span>
                        </div>
                      </div>
                      <div className="rounded-md bg-emerald-50 p-2">
                        <div className="text-[10px] uppercase tracking-wide text-emerald-700">
                          HR
                        </div>
                        <div className="text-lg font-semibold text-emerald-900">
                          {formatMetric(row.HR)}
                          <span className="text-xs font-normal ml-1">bpm</span>
                        </div>
                      </div>
                      <div className="rounded-md bg-violet-50 p-2">
                        <div className="text-[10px] uppercase tracking-wide text-violet-700">
                          HRV
                        </div>
                        <div className="text-xs font-semibold text-violet-900 mt-1 leading-snug">
                          {formatHrv(row.HRV)}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'record' && (
        <>
        <div className="bg-black/40 backdrop-blur-sm rounded-xl border border-white/20 p-4 mb-4">
          <div className="grid grid-cols-2 gap-3 mb-2">
            <label className="text-left text-white/80 text-sm">
              Age
              <input
                type="number"
                min={1}
                max={120}
                value={ageInput}
                onChange={(e) => setAgeInput(e.target.value)}
                disabled={busy || isRecording}
                className="mt-1 w-full rounded-lg px-3 py-2 bg-white/90 text-gray-900"
              />
            </label>
            <label className="text-left text-white/80 text-sm">
              Gender
              <select
                value={genderInput}
                onChange={(e) => setGenderInput(e.target.value)}
                disabled={busy || isRecording}
                className="mt-1 w-full rounded-lg px-3 py-2 bg-white/90 text-gray-900"
              >
                <option value="M">Male (M)</option>
                <option value="F">Female (F)</option>
              </select>
            </label>
          </div>
          <p className="text-white/50 text-xs text-left">
            Demographics are sent with the video to the BP-from-PPG model.
            {hasCalibrationData({
              HR: parseOptionalNumber(calibHR),
              SBP: parseOptionalNumber(calibSBP),
              DBP: parseOptionalNumber(calibDBP),
              Glucose: parseOptionalNumber(calibGlucose),
              HemoPct: parseOptionalNumber(calibHemoPct),
            })
              ? ' Calibration data is set — Analyze BP will call calibration.'
              : ' No calibration data — Analyze BP will call inference.'}
          </p>
        </div>

        <div className="bg-black/40 backdrop-blur-sm rounded-xl border border-white/20 p-4 mb-6 min-h-[220px] flex flex-col items-center justify-center text-center">
          {!isNative && !videoUrl && (
            <video
              ref={webPreviewRef}
              muted
              playsInline
              autoPlay
              className="w-full max-h-72 rounded-lg bg-black mb-3 object-cover"
            />
          )}
          {status && !error && (
            <p className="text-white/90 text-sm mb-2">{status}</p>
          )}
          {isRecording && (
            <div className="flex items-center gap-2 text-red-300 font-semibold text-lg">
              <span className="inline-block w-3 h-3 rounded-full bg-red-500 animate-pulse" />
              {formatDuration(duration)}
              <span className="text-white/50 text-sm font-normal">
                / {TARGET_RECORD_SECONDS}s
              </span>
            </div>
          )}
          {error && <p className="text-red-300 text-sm">{error}</p>}
          {s3Path && (
            <p className="text-white/60 text-xs mt-2 break-all">Uploaded: {s3Path}</p>
          )}
          {videoUrl && !isRecording && (
            <div className="w-full mt-4">
              {previewSrc && (
                <video
                  src={previewSrc}
                  controls
                  className="w-full max-h-64 rounded-lg bg-black"
                />
              )}
            </div>
          )}
          {!isReady && !error && (
            <div className="text-white/70 text-sm">Preparing camera preview…</div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 justify-center mb-8">
          {!isRecording ? (
            <button
              onClick={handleStartRecording}
              disabled={!isReady || busy}
              className="px-6 py-3 rounded-lg bg-red-600 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700 transition-colors"
            >
              Record
            </button>
          ) : (
            <button
              onClick={handleStopRecording}
              className="px-6 py-3 rounded-lg bg-white text-gray-900 font-semibold hover:bg-gray-100 transition-colors"
            >
              Stop
            </button>
          )}
          <button
            onClick={handleFlipCamera}
            disabled={!isReady || isRecording || busy}
            className="px-6 py-3 rounded-lg bg-white/20 text-white font-medium border border-white/30 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/30 transition-colors"
          >
            Flip Camera
          </button>
          <button
            onClick={() => {
              setError('');
              setShowCalibrationDialog(true);
            }}
            disabled={busy || isRecording}
            className="px-6 py-3 rounded-lg bg-amber-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-600 transition-colors"
          >
            Calibration
          </button>
          <button
            onClick={uploadAndAnalyze}
            disabled={!canAnalyze || isRecording || busy}
            className="px-6 py-3 rounded-lg bg-teal-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-teal-600 transition-colors"
          >
            {isUploading
              ? 'Uploading…'
              : isAnalyzing
                ? 'Submitting…'
                : 'Analyze BP'}
          </button>
        </div>

        {submitAck && (
          <div className="bg-white/95 rounded-xl shadow-lg p-5 text-left mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {submitAck.call_type === 'calibration'
                ? 'Calibration submitted'
                : 'Inference submitted'}
            </h2>
            <p className="text-sm text-gray-600 mb-2">
              Processing runs in the background. Personalized HR / SBP / DBP are
              written to <code className="text-xs">faceVideoBP</code> when
              complete.
            </p>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>
                <span className="font-medium">User:</span> {submitAck.userid}
              </li>
              <li>
                <span className="font-medium">Time:</span> {submitAck.datetime}
              </li>
              {submitAck.s3_video_file_path && (
                <li className="break-all">
                  <span className="font-medium">Video:</span>{' '}
                  {submitAck.s3_video_file_path}
                </li>
              )}
            </ul>
          </div>
        )}

        {showCalibrationDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                Calibration
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Enter any cuff / lab measurements you have. Blank fields are sent
                as 0. Saved values are used on the next Analyze BP as a
                calibration job.
              </p>
              <div className="space-y-3">
                <label className="block text-sm text-gray-700">
                  HR
                  <input
                    type="number"
                    min={30}
                    max={220}
                    value={calibHR}
                    onChange={(e) => setCalibHR(e.target.value)}
                    placeholder="bpm"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  Systolic
                  <input
                    type="number"
                    min={60}
                    max={250}
                    value={calibSBP}
                    onChange={(e) => setCalibSBP(e.target.value)}
                    placeholder="mmHg"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  Diastolic
                  <input
                    type="number"
                    min={30}
                    max={150}
                    value={calibDBP}
                    onChange={(e) => setCalibDBP(e.target.value)}
                    placeholder="mmHg"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  Glucose
                  <input
                    type="number"
                    min={20}
                    max={600}
                    value={calibGlucose}
                    onChange={(e) => setCalibGlucose(e.target.value)}
                    placeholder="mg/dL"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </label>
                <label className="block text-sm text-gray-700">
                  Hemoglobin %
                  <input
                    type="number"
                    min={5}
                    max={25}
                    step="0.1"
                    value={calibHemoPct}
                    onChange={(e) => setCalibHemoPct(e.target.value)}
                    placeholder="%"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </label>
              </div>
              <div className="mt-5 flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  onClick={clearCalibrationData}
                  className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setShowCalibrationDialog(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveCalibrationDialog}
                  className="px-4 py-2 rounded-lg bg-amber-500 text-white font-medium hover:bg-amber-600"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {results && (
          <div className="bg-white/95 rounded-xl shadow-lg p-5 text-left">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Results</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <div className="rounded-lg bg-rose-50 p-3">
                <div className="text-xs text-rose-700 uppercase tracking-wide">SBP</div>
                <div className="text-2xl font-bold text-rose-900">
                  {results.sbp != null ? Math.round(Number(results.sbp)) : '—'}
                  <span className="text-sm font-normal ml-1">mmHg</span>
                </div>
              </div>
              <div className="rounded-lg bg-blue-50 p-3">
                <div className="text-xs text-blue-700 uppercase tracking-wide">DBP</div>
                <div className="text-2xl font-bold text-blue-900">
                  {results.dbp != null ? Math.round(Number(results.dbp)) : '—'}
                  <span className="text-sm font-normal ml-1">mmHg</span>
                </div>
              </div>
              <div className="rounded-lg bg-emerald-50 p-3">
                <div className="text-xs text-emerald-700 uppercase tracking-wide">HR</div>
                <div className="text-2xl font-bold text-emerald-900">
                  {results.hr != null ? Math.round(Number(results.hr)) : '—'}
                  <span className="text-sm font-normal ml-1">bpm</span>
                </div>
              </div>
              <div className="rounded-lg bg-violet-50 p-3">
                <div className="text-xs text-violet-700 uppercase tracking-wide">HRV</div>
                <div className="text-sm font-semibold text-violet-900 mt-1">
                  {formatHrv(results.hrv)}
                </div>
              </div>
            </div>

            {ppgSeries.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  PPG waveform ({ppgSeries.length} samples)
                </div>
                <svg
                  viewBox={`0 0 ${Math.max(ppgSeries.length - 1, 1)} 100`}
                  preserveAspectRatio="none"
                  className="w-full h-28 rounded-lg bg-slate-900"
                >
                  {(() => {
                    const min = Math.min(...ppgSeries);
                    const max = Math.max(...ppgSeries);
                    const span = max - min || 1;
                    const points = ppgSeries
                      .map((v, i) => {
                        const y = 95 - ((v - min) / span) * 90;
                        return `${i},${y}`;
                      })
                      .join(' ');
                    return (
                      <polyline
                        fill="none"
                        stroke="#34d399"
                        strokeWidth="1.5"
                        points={points}
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })()}
                </svg>
              </div>
            )}

            {results.demographics_used && (
              <p className="text-xs text-gray-500">
                Demographics used: age {results.demographics_used.age}, sex_male{' '}
                {results.demographics_used.sex_male}
                {results.n_frames != null ? ` · ${results.n_frames} frames` : ''}
              </p>
            )}
          </div>
        )}
        </>
        )}
      </div>

      <style>{`
        .video-exam-page {
          background: transparent;
        }
        body:has(.video-exam-page) {
          background: transparent;
        }
      `}</style>
    </div>
  );
};

export default VideoExam;
